import React, { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Button, CartSummary, type DataChipProps, InfoTooltipAction, Sheet, Switch } from '@pkg/components';
import { type ExpireOption, useTimeAgo } from '@pkg/hooks';
import { formatUsd } from '@pkg/utils';

import { useExchangeRatesStore, useOrdersStore } from '@app/store/zustand';

import { ExpireSelector } from '..//ExpireSelector';
import { AssetCard, getNumberDirection, getNumberSign } from '../AssetCard';
import { assetOrderSheetCopy } from '../AssetOrder.copy';

import { TimestampBadge } from './components';
import { useAssetOrderState } from './useAssetOrderState';

import {
    FooterInterior,
    MainInterior,
    Section,
    SectionHeader,
    SummaryRow,
    TitleLarge,
    TitleMedium,
} from './AssetOrderSheet.style';

export const AssetOrderSheet = ({ onClose }: { onClose: () => void }) => {
    const {
        localCurrency,
        lastSaleUtcMs,
        nativeCurrency,
        formatAndUpdateLocalPrice,
        floorUsdValue,
        floorPercentChange,
        estimationUsd,
        processFeeUsd,
        handleSubmit,
        localPrice,
        nativePrice,
        allMarketplaces,
        localAppraisal,
        usdPrice,
        creatorFeeUsd,
        handlePriceBlur,
        handlePriceChange,
        handleLocalCurrencyChange,
        handleAllMarketplacesToggle,
        floorUsd24hChange,
        onSetExpiration,
        lastSaleUsd,
    } = useAssetOrderState();
    const [translate] = useTranslation('marketplace');
    const [expirationType, setExpirationType] = useState<ExpireOption>('week');

    const { token, orderType } = useOrdersStore(useShallow((state) => state));
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    const { label: lastSaleTimeAgo } = useTimeAgo(lastSaleUtcMs, { preferDaysOverWeeks: true });
    const lastSaleLabel = lastSaleUtcMs ? `${formatUsd(lastSaleUsd, true)} | ${lastSaleTimeAgo.shorthand}` : undefined;

    const statsList = useMemo(() => {
        const updatedStatsList: DataChipProps[] = [];
        function setPriceFromStat(usdValue = 0) {
            const converted = convertCurrency('usd', localCurrency, usdValue, true);
            formatAndUpdateLocalPrice(converted, localCurrency);
        }
        const floorPriceSuffixData = floorUsd24hChange
            ? [
                  getNumberSign(floorUsd24hChange),
                  formatUsd(Math.abs(floorUsd24hChange), true),
                  ` (${floorPercentChange}%)`,
              ]
            : [];
        updatedStatsList.push({
            label: translate('common-chip-price'),
            value: formatUsd(floorUsdValue, true),
            suffix: floorPriceSuffixData.join(''),
            suffixType: getNumberDirection(floorUsd24hChange),
            ariaLabel: translate('common-chip-price-a11y'),
            onAction: () => setPriceFromStat(floorUsdValue),
        });

        if (lastSaleLabel) {
            updatedStatsList.push({
                label: translate('common-chip-last'),
                value: lastSaleLabel,
                ariaLabel: translate('common-chip-last-a11y'),
                onAction: () => setPriceFromStat(lastSaleUsd),
            });
        }

        return updatedStatsList;
    }, [
        convertCurrency,
        floorPercentChange,
        floorUsd24hChange,
        floorUsdValue,
        formatAndUpdateLocalPrice,
        lastSaleLabel,
        lastSaleUsd,
        localCurrency,
        translate,
    ]);
    function handleExpirationSelect(expiration, expirationTime) {
        setExpirationType(expiration);
        onSetExpiration(expirationTime);
    }

    async function handleSubmitOrder() {
        await handleSubmit();
        onClose();
    }
    const orderCopy = assetOrderSheetCopy[orderType || 'list'];

    const assetDetailsSection = token ? (
        <Section>
            <AssetCard
                asset={token}
                localCurrency={localCurrency}
                localPrice={localPrice}
                nativeCurrency={nativeCurrency}
                nativePrice={nativePrice}
                appraisal={localAppraisal}
                onPriceBlur={handlePriceBlur}
                onPriceChange={handlePriceChange}
                onLocalCurrencyChange={handleLocalCurrencyChange}
            >
                <AssetCard.StatsList items={statsList} />
            </AssetCard>
        </Section>
    ) : null;

    const orderPeriodSection = (
        <Section>
            <SectionHeader>
                <TitleMedium>{orderCopy.period}</TitleMedium>

                <InfoTooltipAction
                    message="TODO: Also requires tooltip!"
                    aria-label={translate('common-info-action-a11y')}
                />
            </SectionHeader>

            <ExpireSelector selected={expirationType} onSelect={handleExpirationSelect} />
            {orderType !== 'offer' ? (
                <Switch
                    id="AssetListingSheet-Switch-Marketplaces"
                    label={translate('asset-listing-sheet-marketplace-toggle-label')}
                    value="asset-listing-sheet-switch-marketplaces"
                    checked={allMarketplaces}
                    onCheckedChange={handleAllMarketplacesToggle}
                />
            ) : null}
        </Section>
    );

    const summaryFooterRow =
        orderType === 'list' ? (
            <SummaryRow>
                <TitleLarge>{translate('asset-listing-sheet-total')}</TitleLarge>
                <TitleLarge>{formatUsd(estimationUsd)}</TitleLarge>
            </SummaryRow>
        ) : null;

    const summarySection =
        orderType === 'list' ? (
            <Section $alignEnd>
                <SummaryRow>
                    <TitleLarge>{translate('asset-listing-sheet-summary')}</TitleLarge>

                    <TimestampBadge timestamp={Date.now() - 9999} />
                </SummaryRow>

                <CartSummary
                    items={[
                        {
                            label: translate('asset-listing-sheet-summary-item-price'),
                            value: formatUsd(usdPrice),
                        },
                        {
                            label: translate('asset-listing-sheet-summary-item-creator'),
                            value: formatUsd(creatorFeeUsd),
                            disabled: true,
                        },
                        {
                            label: translate('asset-listing-sheet-summary-item-fee'),
                            value: formatUsd(processFeeUsd),
                            disabled: true,
                        },
                    ]}
                />
            </Section>
        ) : null;

    return (
        <Sheet gapless backdrop onClose={onClose}>
            <Sheet.Header largeTitle title={orderCopy.title} onClose={onClose} />
            <Sheet.Main>
                <Sheet.Layout>
                    <Sheet.Layout.Columns stretch>
                        <Sheet.Layout.Columns.Primary padded scrollable>
                            <MainInterior>
                                {assetDetailsSection}
                                {orderPeriodSection}
                                {summarySection}
                            </MainInterior>
                        </Sheet.Layout.Columns.Primary>
                    </Sheet.Layout.Columns>

                    <Sheet.Layout.Footer>
                        <FooterInterior>
                            {summaryFooterRow}
                            <Button fluid size="large" variant="solid" onClick={handleSubmitOrder}>
                                {orderCopy.ctaCopy}
                            </Button>
                        </FooterInterior>
                    </Sheet.Layout.Footer>
                </Sheet.Layout>
            </Sheet.Main>
        </Sheet>
    );
};
